import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { db } from '../../config/firebaseConfig';
import {
  FaUsers,
  FaEye,
  FaEyeSlash,
  FaTimes,
  FaSearch,
  FaInfoCircle,
  FaFileExcel,
} from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import { useUserProfile } from '../../contexts/UserProfileContext';
import ProjectDetailModal, {
  PATIENT_STATUSES,
  buildPatientSummary,
  formatDate,
} from './ProjectDetailModal';
import { isPatientOwner, normalizePatientId } from '../../utils/patientAccess';
import styles from './ProjectsModal.module.css';

// Firestore "in" sorgusu tek seferde en fazla 30 değer kabul ediyor
const IN_QUERY_LIMIT = 30;

const chunk = (items, size) => {
  const result = [];
  for (let i = 0; i < items.length; i += size) {
    result.push(items.slice(i, i + size));
  }
  return result;
};

const ProjectsModal = ({ employee, onClose }) => {
  const { isDarkMode } = useTheme();
  const { userAuthorization } = useUserProfile();

  // Check if user is admin
  const isAdmin = userAuthorization.includes('admin') || userAuthorization.includes('Kısıtlı Admin');

  const [projects, setProjects] = useState([]);
  const [showAssignedOnly, setShowAssignedOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [assignmentStatusFilter, setAssignmentStatusFilter] = useState('all');
  const [projectStatusFilter, setProjectStatusFilter] = useState('all');
  const [detailProject, setDetailProject] = useState(null);
  const [exporting, setExporting] = useState(false);
  const modalRef = useRef(null);

  // Close modal when clicking outside
  const handleOverlayClick = useCallback((e) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  }, [onClose]);

  // Add escape key listener to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      // Detay popup'ı açıkken Escape önce onu kapatsın
      if (e.key === 'Escape' && !detailProject) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, detailProject]);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const querySnapshot = await getDocs(collection(db, 'Projects'));
        const projectsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProjects(projectsData);
      } catch (error) {
        console.error('Error fetching projects:', error);
        toast.error('Projeler yüklenirken bir hata oluştu!');
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [employee]);

  // Çalışanın atama kayıtlarını proje bazında grupla (merkez/rol başına ayrı kayıt tutuluyor)
  const assignmentsByProject = useMemo(() => {
    const map = new Map();
    (employee.projects || []).forEach(assignment => {
      if (!assignment?.id) return;
      const list = map.get(assignment.id) || [];
      list.push(assignment);
      map.set(assignment.id, list);
    });
    return map;
  }, [employee.projects]);

  // Tablo satırları: proje + çalışanın o projedeki görev bilgileri
  const rows = useMemo(() => {
    const source = showAssignedOnly
      ? projects.filter(project => assignmentsByProject.has(project.id))
      : projects;

    return source.map(project => {
      const assignments = assignmentsByProject.get(project.id) || [];
      // Herhangi bir merkezde aktif görev varsa çalışan bu projede aktif sayılır
      const hasActive = assignments.some(a => a.status === 'active' || !a.status);
      const fteAssignment = assignments.find(a => a.rate?.type?.toUpperCase() === 'FTE');
      const rateType = (fteAssignment || assignments[0])?.rate?.type?.toUpperCase();

      let fteValue = '-';
      if (assignments.length > 0) {
        fteValue = rateType === 'FTE' ? (fteAssignment?.rate?.value ?? '-') : 'Saatlik';
      }

      return {
        project,
        assignments,
        assignmentStatus: assignments.length === 0 ? null : hasActive ? 'active' : 'passive',
        projectStatus: project.projectStatus || 'Aktif',
        fteValue,
      };
    });
  }, [projects, assignmentsByProject, showAssignedOnly]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return rows.filter(row => {
      if (assignmentStatusFilter !== 'all' && row.assignmentStatus !== assignmentStatusFilter) {
        return false;
      }
      if (projectStatusFilter !== 'all' && row.projectStatus !== projectStatusFilter) {
        return false;
      }
      if (!term) return true;

      const { project, assignments } = row;
      const haystack = [
        project.titleShort,
        project.titleLong,
        project.protocolNumber,
        project.customer,
        project.sbProjectNumber,
        ...assignments.map(a => a.centerName),
        ...assignments.map(a => a.role),
      ];

      return haystack.some(value => value && String(value).toLowerCase().includes(term));
    });
  }, [rows, searchTerm, assignmentStatusFilter, projectStatusFilter]);

  const truncateText = (text, limit = 40) => {
    if (!text) return '-';
    return text.length > limit ? `${text.substring(0, limit)}...` : text;
  };

  const formatRate = (rate) => {
    if (!rate) return '-';
    const type = rate.type?.toUpperCase() === 'FTE' ? 'FTE' : 'Saatlik';
    const amount = rate.value ?? rate.price;
    if (amount === undefined || amount === null || amount === '') return type;
    return `${type}: ${amount}${rate.currency ? ` ${rate.currency}` : ''}`;
  };

  const unique = (values) => [...new Set(values.filter(Boolean))].join(', ') || '-';

  // Filtrelenmiş listeyi hasta/ziyaret özetleriyle birlikte Excel'e aktarır
  const handleExport = useCallback(async () => {
    if (filteredRows.length === 0) {
      toast.info('Dışa aktarılacak proje bulunmamaktadır.');
      return;
    }

    setExporting(true);
    try {
      const projectIds = filteredRows.map(row => row.project.id);
      const patientsByProject = new Map();
      const visitsByProject = new Map();

      await Promise.all(
        chunk(projectIds, IN_QUERY_LIMIT).map(async (ids) => {
          const [patientsSnap, visitsSnap] = await Promise.all([
            getDocs(query(collection(db, 'Patients'), where('projectId', 'in', ids))),
            getDocs(query(collection(db, 'Visits'), where('projectId', 'in', ids))),
          ]);

          patientsSnap.docs.forEach(docSnap => {
            const data = docSnap.data();
            const list = patientsByProject.get(data.projectId) || [];
            list.push(data);
            patientsByProject.set(data.projectId, list);
          });

          visitsSnap.docs.forEach(docSnap => {
            const data = docSnap.data();
            const list = visitsByProject.get(data.projectId) || [];
            list.push(data);
            visitsByProject.set(data.projectId, list);
          });
        })
      );

      const excelRows = filteredRows.map(({ project, assignments, assignmentStatus, projectStatus }) => {
        // Çıktı bu çalışanın raporu: proje geneli değil, çalışanın kendi
        // hastaları (sahip olduğu + ziyaret ettiği) ve kendi ziyaretleri.
        const visits = (visitsByProject.get(project.id) || []).filter(
          v => v.employeeId === employee.id
        );
        const visitedPatientIds = new Set(
          visits.map(v => normalizePatientId(v.patientId)).filter(Boolean)
        );
        const employeePatients = (patientsByProject.get(project.id) || []).filter(
          patient =>
            isPatientOwner(patient, employee.id) ||
            visitedPatientIds.has(normalizePatientId(patient.patientId))
        );
        const patientSummary = buildPatientSummary(employeePatients);

        const row = {
          'Proje Kodu': project.titleShort || '-',
          'Protokol Numarası': project.protocolNumber || '-',
          'Müşteri': project.customer || '-',
          'Proje Adı': project.titleLong || '-',
          'Proje Tarihi': formatDate(project.projectDate),
          'Proje Bitiş Tarihi': formatDate(project.projectDueDate),
          'Proje Durumu': projectStatus,
          'Görev Durumu':
            assignmentStatus === 'active' ? 'Aktif' : assignmentStatus === 'passive' ? 'Pasif' : '-',
          'Merkezler': unique(assignments.map(a => a.centerName)),
          'Roller': unique(assignments.map(a => a.role)),
          'Çalışan Tipi': unique(assignments.map(a => a.workerType)),
          'Körleme Durumu': unique(assignments.map(a => a.blindStatus)),
          'Atanma Tarihi': assignments.length ? formatDate(assignments[0].assignedAt) : '-',
        };

        if (isAdmin) {
          row['Ücret / FTE'] = unique(assignments.map(a => formatRate(a.rate)));
        }

        row['Çalışanın Hastası'] = patientSummary.total;
        row['Ev Ziyaretli Hasta'] = patientSummary.homeVisit;
        Object.entries(PATIENT_STATUSES).forEach(([key, meta]) => {
          row[`Hasta - ${meta.label}`] = patientSummary.byStatus[key] || 0;
        });
        row['Çalışanın Ziyareti'] = visits.length;

        return row;
      });

      const worksheet = XLSX.utils.json_to_sheet(excelRows);
      worksheet['!cols'] = Object.keys(excelRows[0]).map(key => ({
        wch: Math.min(Math.max(key.length + 2, 12), 45),
      }));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Projeler');

      const employeeName = `${employee.eName || ''}_${employee.eSurname || ''}`.trim().replace(/\s+/g, '_');
      const today = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `Projeler_${employeeName || 'Calisan'}_${today}.xlsx`);

      toast.success('Rapor başarıyla oluşturuldu!');
    } catch (error) {
      console.error('Error exporting projects report:', error);
      toast.error('Rapor oluşturulurken bir hata oluştu!');
    } finally {
      setExporting(false);
    }
  }, [filteredRows, employee, isAdmin]);

  const columnCount = isAdmin ? 8 : 7;

  return (
    <div
      className={styles.modalOverlay}
      ref={modalRef}
      onClick={handleOverlayClick}
      data-theme={isDarkMode ? 'dark' : 'light'}
    >
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <h2 className={styles.modalTitle}>
              <FaUsers />
              Projeler - {employee.eName} {employee.eSurname}
            </h2>
            <div className={styles.headerActions}>
              <button
                className={styles.toggleButton}
                onClick={() => setShowAssignedOnly(prev => !prev)}
                title={showAssignedOnly ? 'Tüm projeleri göster' : 'Sadece atanan projeleri göster'}
              >
                {showAssignedOnly ? <FaEye /> : <FaEyeSlash />}
                {showAssignedOnly ? 'Atanan Projeler' : 'Tüm Projeler'}
              </button>
              <button
                className={styles.exportButton}
                onClick={handleExport}
                disabled={exporting || loading}
              >
                <FaFileExcel />
                {exporting ? 'Hazırlanıyor...' : 'Rapor Çıktısı'}
              </button>
            </div>
          </div>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Kapat"
          >
            <FaTimes />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.filterBar}>
            <div className={styles.searchWrapper}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Proje kodu, protokol, proje adı, müşteri, merkez veya rol ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className={styles.clearSearch}
                  onClick={() => setSearchTerm('')}
                  aria-label="Aramayı temizle"
                >
                  <FaTimes />
                </button>
              )}
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Görev Durumu</label>
              <select
                className={styles.filterSelect}
                value={assignmentStatusFilter}
                onChange={(e) => setAssignmentStatusFilter(e.target.value)}
              >
                <option value="all">Tümü</option>
                <option value="active">Aktif</option>
                <option value="passive">Pasif</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Proje Durumu</label>
              <select
                className={styles.filterSelect}
                value={projectStatusFilter}
                onChange={(e) => setProjectStatusFilter(e.target.value)}
              >
                <option value="all">Tümü</option>
                <option value="Aktif">Aktif</option>
                <option value="Pasif">Pasif</option>
              </select>
            </div>

            <span className={styles.resultCount}>{filteredRows.length} proje</span>
          </div>

          <div className={styles.tableContainer}>
            <table className={styles.projectsTable}>
              <thead>
                <tr className={styles.tableHeader}>
                  <th>Proje Kodu</th>
                  <th>Protokol Numarası</th>
                  <th>Proje Tarihi</th>
                  <th>Proje Adı</th>
                  <th>Görev Durumu</th>
                  <th>Proje Durumu</th>
                  {isAdmin && <th>FTE</th>}
                  <th>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columnCount} className={styles.emptyMessage}>
                      Projeler yükleniyor...
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={columnCount} className={styles.emptyMessage}>
                      {rows.length === 0
                        ? showAssignedOnly
                          ? 'Bu çalışana atanmış proje bulunmamaktadır.'
                          : 'Hiç proje bulunmamaktadır.'
                        : 'Arama ve filtre kriterlerine uyan proje bulunamadı.'}
                    </td>
                  </tr>
                ) : (
                  filteredRows.map(({ project, assignments, assignmentStatus, projectStatus, fteValue }) => (
                    <tr
                      key={project.id}
                      className={`${styles.tableRow} ${
                        assignmentStatus === 'passive' ? styles.passiveProject : ''
                      }`}
                    >
                      <td className={`${styles.tableCell} ${styles.projectCode}`}>
                        {project.titleShort || '-'}
                      </td>
                      <td className={`${styles.tableCell} ${styles.protocolNumber}`}>
                        {project.protocolNumber || '-'}
                      </td>
                      <td className={`${styles.tableCell} ${styles.projectDate}`}>
                        {formatDate(project.projectDate)}
                      </td>
                      <td
                        className={`${styles.tableCell} ${styles.projectName}`}
                        title={project.titleLong}
                      >
                        {truncateText(project.titleLong)}
                      </td>
                      <td className={`${styles.tableCell} ${styles.statusCell}`}>
                        {assignmentStatus ? (
                          <span
                            className={`${styles.statusBadge} ${
                              assignmentStatus === 'active'
                                ? styles.activeBadge
                                : styles.passiveBadge
                            }`}
                          >
                            {assignmentStatus === 'active' ? 'Aktif' : 'Pasif'}
                          </span>
                        ) : (
                          <span className={styles.mutedText}>Atanmamış</span>
                        )}
                      </td>
                      <td className={`${styles.tableCell} ${styles.statusCell}`}>
                        <span
                          className={`${styles.statusBadge} ${
                            projectStatus === 'Aktif' ? styles.activeBadge : styles.passiveBadge
                          }`}
                        >
                          {projectStatus}
                        </span>
                      </td>
                      {isAdmin && (
                        <td className={`${styles.tableCell} ${styles.projectFte}`}>
                          {fteValue}
                        </td>
                      )}
                      <td className={`${styles.tableCell} ${styles.actionCell}`}>
                        <button
                          className={styles.detailButton}
                          onClick={() => setDetailProject({ project, assignments })}
                        >
                          <FaInfoCircle />
                          Detay
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {detailProject && (
        <ProjectDetailModal
          project={detailProject.project}
          assignments={detailProject.assignments}
          employee={employee}
          isAdmin={isAdmin}
          onClose={() => setDetailProject(null)}
        />
      )}
    </div>
  );
};

export default ProjectsModal;
